import { parseHomeworkVoiceSelection } from "./homeworkVoiceSelection";

export interface GptLiveInputContext {
  // Changes on a new chat or authoritative assistant response, not on loading.
  scopeKey: string;
  homeworkChoices?: readonly string[];
}

interface Delegation {
  delegationId: string;
  offsetMs: number | null;
}

interface Options {
  getContext: () => GptLiveInputContext;
  onDelegation: (event: Delegation & { transcript: string }) => void | Promise<void>;
  onHomeworkChoice: (selection: string) => boolean;
  onGameTurn: (transcript: string) => boolean;
  onDuplicate: (delegationId: string, reason?: string) => void;
  onError: (error: unknown) => void;
}

const clean = (text: string) => text.replace(
  /\[\s*(?:tongue[\s-]*click|inhale|exhale|breath(?:ing)?|sigh|laugh(?:ing)?|cough(?:ing)?|sniff(?:ing)?|pause|silence)\s*\]/gi, " ",
).replace(/\s+/g, " ").trim();

// Own a user's transcript independently of concurrent assistant audio deltas.
export class GptLiveInputRouter {
  private context: GptLiveInputContext | null = null;
  private text = "";
  private consumed = false;
  private lastEndMs: number | null = null;
  private assistantFollowed = false;
  private pending: Delegation | null = null;
  private pendingAt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private seenDelegations = new Set<string>();
  private selectedScopes = new Map<string, readonly string[]>();

  constructor(private options: Options) {}

  clear() {
    this.cancelTimer();
    this.context = null;
    this.text = "";
    this.consumed = false;
    this.lastEndMs = null;
    this.assistantFollowed = false;
    this.pending = null;
    this.seenDelegations.clear();
    this.selectedScopes.clear();
  }

  private cancelTimer() {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  private syncContext() {
    const next = this.options.getContext();
    if (this.context?.scopeKey !== next.scopeKey) {
      this.cancelTimer();
      this.text = "";
      this.consumed = false;
      this.lastEndMs = null;
      this.assistantFollowed = false;
      this.pending = null;
    }
    this.context = next;
    return next;
  }

  private choices() {
    return this.context?.homeworkChoices ??
      this.selectedScopes.get(this.context?.scopeKey ?? "") ?? [];
  }

  input(delta: string, endMs: number | null) {
    this.syncContext();
    // Late punctuation belongs to the consumed answer, not a new user turn.
    if (this.consumed && !/[a-z0-9]/i.test(clean(delta))) return;
    if (this.consumed || (
      // Preserve the existing turn boundary for ordinary conversation.
      // A pending delegation or active homework list owns its fragments,
      // even if assistant output arrives between them.
      this.assistantFollowed && !this.choices().length && !this.pending
    )) {
      this.text = "";
      this.consumed = false;
      this.assistantFollowed = false;
    }
    this.text += delta;
    this.lastEndMs = endMs;
    this.schedule(this.pending ? 300 : 1800);
  }

  output(startMs: number | null) {
    if (this.text.trim() && (
      startMs === null || this.lastEndMs === null || startMs >= this.lastEndMs - 150
    )) this.assistantFollowed = true;
  }

  delegation(delegationId: string, offsetMs: number | null) {
    this.syncContext();
    if (!delegationId) return;
    if (this.seenDelegations.has(delegationId)) {
      this.options.onDuplicate(delegationId);
      return;
    }
    if (this.pending && this.pending.delegationId !== delegationId) {
      this.options.onDuplicate(delegationId);
      return;
    }
    this.pending = { delegationId, offsetMs };
    this.pendingAt = Date.now();
    // An early delegation stays pending until transcript fragments arrive.
    // Even normal delegations allow trailing transcript fragments to settle.
    this.schedule(300);
  }

  private schedule(delay: number) {
    this.cancelTimer();
    const scope = this.context?.scopeKey;
    this.timer = setTimeout(() => {
      this.timer = null;
      if (this.options.getContext().scopeKey !== scope) {
        this.syncContext();
        return;
      }
      try { this.flush(); } catch (error) { this.options.onError(error); }
    }, delay);
  }

  private flush() {
    const transcript = clean(this.text);
    if (this.consumed) {
      if (this.pending) {
        const knownOldOffset = this.pending.offsetMs !== null && this.lastEndMs !== null &&
          this.pending.offsetMs <= this.lastEndMs;
        const remaining = 5000 - (Date.now() - this.pendingAt);
        if (!knownOldOffset && remaining > 0) this.schedule(remaining);
        else {
          this.options.onDuplicate(this.pending.delegationId);
          this.pending = null;
        }
      }
      return;
    }
    if (!transcript) {
      if (this.pending) {
        const remaining = 5000 - (Date.now() - this.pendingAt);
        if (remaining > 0) this.schedule(remaining);
        else {
          this.options.onDuplicate(this.pending.delegationId,
            "No user transcript was available for this delegation. Do not take an action.");
          this.pending = null;
        }
      }
      return;
    }
    // Read current choices too: the awaitKey can arrive just after the list.
    this.context = this.options.getContext();
    const choices = this.choices();
    const selection = parseHomeworkVoiceSelection(transcript, choices);
    const scope = this.context.scopeKey;
    if (selection && this.selectedScopes.has(scope)) {
      if (this.pending) this.options.onDuplicate(this.pending.delegationId);
      this.pending = null;
      this.consumed = true;
      return;
    }
    if (this.pending) {
      const pending = this.pending;
      this.pending = null;
      this.consumed = true;
      this.seenDelegations.add(pending.delegationId);
      if (selection) this.selectedScopes.set(scope, choices);
      void Promise.resolve(this.options.onDelegation({
        ...pending, transcript: selection ?? transcript,
      })).catch(this.options.onError);
    } else if (selection && this.options.onHomeworkChoice(selection)) {
      this.selectedScopes.set(scope, choices);
      this.consumed = true;
    } else if (this.options.onGameTurn(transcript)) {
      this.consumed = true;
    }
  }
}
